<template>
  <AIVDialog
    v-model="isVisible"
    :title="attachment.original_filename"
    width="90%"
  >
    <!-- Image preview -->
    <img
      v-if="previewType === 'image'"
      :src="blobUrl"
      :alt="attachment.original_filename"
      style="max-width: 100%; max-height: 70vh; display: block; margin: auto;"
    />

    <!-- PDF preview -->
    <div v-else-if="previewType === 'pdf'">
      <div v-if="pdfLoading" style="text-align: center; padding: 40px;">
        <i class="fa-solid fa-spinner fa-spin fa-2x"></i>
      </div>
      <embed
        v-else-if="pdfBlobUrl"
        :src="pdfBlobUrl"
        type="application/pdf"
        width="100%"
        style="height: 70vh;"
      />
    </div>

    <!-- Text preview -->
    <div v-else-if="previewType === 'text'">
      <div v-if="previewLoading" style="text-align: center; padding: 40px;">
        <i class="fa-solid fa-spinner fa-spin fa-2x"></i>
      </div>
      <pre v-else class="text-preview">{{ previewText }}</pre>
    </div>

    <!-- Audio preview -->
    <audio
      v-else-if="previewType === 'audio'"
      controls
      :src="blobUrl"
      style="width: 100%;"
    ></audio>

    <!-- Video preview -->
    <video
      v-else-if="previewType === 'video'"
      controls
      :src="blobUrl"
      style="max-width: 100%; max-height: 70vh;"
    ></video>

    <!-- No preview available -->
    <div
      v-else
      style="text-align: center; padding: 40px; color: rgba(255,255,255,0.6);"
    >
      <i class="fa-solid fa-file fa-3x" style="margin-bottom: 16px; display: block;"></i>
      This file cannot be previewed.
    </div>

    <template slot="footer">
      <button class="btn btn-primary" :disabled="downloadLoading" @click="downloadAttachment">
        <i :class="downloadLoading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-download'"></i>
        {{ downloadLoading ? 'Downloading…' : 'Download' }}
      </button>
    </template>
  </AIVDialog>
</template>

<script>
export default {
  name: "AttachmentPreviewModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    attachment: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      previewText: null,
      previewLoading: false,
      pdfBlobUrl: null,
      pdfLoading: false,
      blobUrl: null,
      downloadLoading: false,
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    downloadUrl() {
      return `${process.env.VUE_APP_API_URL}/api/conversations/attachments/${this.attachment.id}/download`;
    },
    previewType() {
      const mt = this.attachment.mimetype;
      if (!mt) return "none";
      if (mt.startsWith("image/")) return "image";
      if (mt === "application/pdf") return "pdf";
      if (mt === "text/plain") return "text";
      if (mt.startsWith("audio/")) return "audio";
      if (mt.startsWith("video/")) return "video";
      return "none";
    },
  },
  watch: {
    isVisible(val) {
      if (val) {
        if (this.previewType === "text") this.fetchTextPreview();
        if (this.previewType === "pdf") this.fetchPdfBlob();
        if (["image", "audio", "video"].includes(this.previewType)) this.fetchMediaBlob();
      } else {
        if (this.pdfBlobUrl) {
          URL.revokeObjectURL(this.pdfBlobUrl);
          this.pdfBlobUrl = null;
        }
        if (this.blobUrl) {
          URL.revokeObjectURL(this.blobUrl);
          this.blobUrl = null;
        }
      }
    },
  },
  methods: {
    fetchPdfBlob() {
      this.pdfLoading = true;
      const token = localStorage.getItem('jwtToken');
      fetch(this.downloadUrl, { headers: { Authorization: token } })
        .then((r) => r.blob())
        .then((blob) => {
          this.pdfBlobUrl = URL.createObjectURL(blob);
        })
        .finally(() => {
          this.pdfLoading = false;
        });
    },
    fetchMediaBlob() {
      const token = localStorage.getItem('jwtToken');
      fetch(this.downloadUrl, { headers: { Authorization: token } })
        .then((r) => r.blob())
        .then((blob) => {
          this.blobUrl = URL.createObjectURL(blob);
        });
    },
    downloadAttachment() {
      this.downloadLoading = true;
      const token = localStorage.getItem('jwtToken');
      fetch(this.downloadUrl, { headers: { Authorization: token } })
        .then(r => r.blob())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = this.attachment.original_filename;
          a.click();
          URL.revokeObjectURL(url);
        })
        .finally(() => { this.downloadLoading = false; });
    },
    fetchTextPreview() {
      this.previewLoading = true;
      const token = localStorage.getItem('jwtToken');
      fetch(this.downloadUrl, { headers: { Authorization: token } })
        .then((r) => r.text())
        .then((text) => {
          this.previewText = text;
        })
        .finally(() => {
          this.previewLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.text-preview {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(0, 0, 0, 0.2);
  padding: 16px;
  border-radius: 6px;
  max-height: 70vh;
  overflow-y: auto;
}
</style>
